/**
 * RBAC v2 — the frontend mirror of backend core/rbac.py (docs/rbac-v2-spec.md).
 *
 * Mirrored VALUE-FOR-VALUE with the backend so the UI never offers a control
 * the API will reject (or hides one the API would allow). When rbac.py changes,
 * this file changes in lockstep.
 *
 * Management tier + planner grant + project designation:
 *     ORG tier      (from /api/me .role):        super_admin > admin > member
 *     PLANNER grant (from /api/me .is_planner):  creates/owns projects
 *     PROJECT axis  (from project membership):   plan(owner) > collaborate > view
 *
 * Pure module: NO React, no DOM, no network. Fully testable in isolation.
 */

// ---------------------------------------------------------------------------
// ORG axis — lives on the user document (UserRole in rbac.py)
// ---------------------------------------------------------------------------

/** Org-level roles. Mirrors rbac.py UserRole. */
const ORG_ROLE = Object.freeze({
  SUPER_ADMIN: 'super_admin', // platform-wide, org-less, unrestricted
  ADMIN: 'admin',             // user management across the projects — ZERO project data
  MEMBER: 'member',           // sees nothing until a planner invites them
});

/** Mirrors rbac.py ORG_ROLE_LEVEL. */
const ORG_ROLE_LEVEL = Object.freeze({
  [ORG_ROLE.SUPER_ADMIN]: 3,
  [ORG_ROLE.ADMIN]: 2,
  [ORG_ROLE.MEMBER]: 1,
});

// ---------------------------------------------------------------------------
// PROJECT axis — lives on the project_members junction (ProjectRole in rbac.py)
// ---------------------------------------------------------------------------

/** Per-project designations. Mirrors rbac.py ProjectRole. PLAN = full project
 * powers: the owner (projects.owner_id) AND grantable co-planners (v2.1). */
const PROJECT_ROLE = Object.freeze({
  VIEW: 'view',
  COLLABORATE: 'collaborate',
  PLAN: 'plan',
});

/** Designations a member-manager (owner, co-planner, or org admin) may grant.
 * Mirrors rbac.py GRANTABLE_PROJECT_ROLES. v2.1: PLAN is grantable as a
 * CO-planner, but only to a user who already holds the org planner grant
 * (is_planner) — that target precondition is enforced by the API, not here. */
const GRANTABLE_PROJECT_ROLES = Object.freeze([PROJECT_ROLE.VIEW, PROJECT_ROLE.COLLABORATE, PROJECT_ROLE.PLAN]);

/** Mirrors rbac.py PROJECT_ROLE_LEVEL. */
const PROJECT_ROLE_LEVEL = Object.freeze({
  [PROJECT_ROLE.VIEW]: 1,
  [PROJECT_ROLE.COLLABORATE]: 2,
  [PROJECT_ROLE.PLAN]: 3,
});

// ---------------------------------------------------------------------------
// Actions — every capability the product gates. Mirrors rbac.py Action.
// ---------------------------------------------------------------------------

const Action = Object.freeze({
  // --- project-work (require project level >= ACTION_REQUIRED_LEVEL) ---
  READ: 'read',
  COMMENT: 'comment',
  EDIT_ACTUALS: 'edit_actuals',       // actual start / finish, progress
  EDIT_ACTIVITY: 'edit_activity',     // other activity fields
  EDIT_STRUCTURE: 'edit_structure',   // WBS, baselines, scenarios
  MANAGE_SCHEDULES: 'manage_schedules', // create / delete / archive schedules
  IMPORT_EXPORT: 'import_export',
  MANAGE_MEMBERS: 'manage_members',   // invite/remove members — project PLAN (owner/co-planner) OR org admin
  // --- planner grant ---
  CREATE_PROJECT: 'create_project',   // create/import a project — planner grant only
  // --- admin tier ---
  MANAGE_USERS: 'manage_users',       // org-wide user management, role grants
  // --- super_admin only ---
  CROSS_PROJECT: 'cross_project',     // org/platform-wide project data access
});

/** Admin-tier actions. Mirrors rbac.py ADMIN_ACTIONS (v2: MANAGE_USERS only). */
const ADMIN_ACTIONS = Object.freeze(new Set([
  Action.MANAGE_USERS,
]));

/** Minimum project level for each project-work action. Mirrors rbac.py ACTION_REQUIRED_LEVEL. */
const ACTION_REQUIRED_LEVEL = Object.freeze({
  [Action.READ]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.VIEW],
  [Action.COMMENT]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.VIEW],
  [Action.EDIT_ACTUALS]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.COLLABORATE],
  [Action.EDIT_ACTIVITY]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.PLAN],
  [Action.EDIT_STRUCTURE]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.PLAN],
  [Action.MANAGE_SCHEDULES]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.PLAN],
  [Action.IMPORT_EXPORT]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.PLAN],
  [Action.MANAGE_MEMBERS]: PROJECT_ROLE_LEVEL[PROJECT_ROLE.PLAN],
});

// ---------------------------------------------------------------------------
// The one authorization decision. Mirrors rbac.py can() branch-for-branch.
// ---------------------------------------------------------------------------

/**
 * The single authorization decision — exact mirror of rbac.py `can`.
 *
 *     super_admin    -> everything (unrestricted, org-less platform level)
 *     CREATE_PROJECT -> the planner grant, regardless of tier (pure admin: false)
 *     MANAGE_USERS   -> admin tier
 *     CROSS_PROJECT  -> nobody (super_admin already returned true)
 *     MANAGE_MEMBERS -> DUAL-AXIS: the org admin tier passes (membership only,
 *                       not project data), OR a project PLAN row (owner or
 *                       co-planner) passes the level check below
 *     project work   -> project level high enough
 *
 * `projectRole == null` means "no membership on this project". That is also
 * exactly what a pure admin looks like on project content, so a pure admin
 * correctly gets `false` for every project-DATA action — only MANAGE_MEMBERS is
 * granted to the admin tier, and that is membership management, not data.
 *
 * @param {string} action - one of Action.*
 * @param {{ orgRole: string, isPlanner?: boolean, projectRole?: string|null }} ctx
 * @returns {boolean}
 */
function can(action, { orgRole, isPlanner = false, projectRole = null } = {}) {
  if (orgRole === ORG_ROLE.SUPER_ADMIN) return true;
  if (action === Action.CREATE_PROJECT) return !!isPlanner;
  if (ADMIN_ACTIONS.has(action)) return orgRole === ORG_ROLE.ADMIN;
  if (action === Action.CROSS_PROJECT) return false;
  // MANAGE_MEMBERS is dual-axis (v2.1): the org admin manages membership on any
  // project in their org. Membership ONLY — every other action falls through to
  // the level check, where a pure admin (projectRole == null) still gets false.
  if (action === Action.MANAGE_MEMBERS && orgRole === ORG_ROLE.ADMIN) return true;
  if (projectRole == null) return false;
  return PROJECT_ROLE_LEVEL[projectRole] >= ACTION_REQUIRED_LEVEL[action];
}

// ---------------------------------------------------------------------------
// Org-role helpers (kept for callers that branch on the org axis).
// ---------------------------------------------------------------------------

/** Valid org role names. */
const VALID_ORG_ROLES = Object.values(ORG_ROLE);

/** Numeric org-axis level (higher = more privileged). 0 if unknown. */
function getOrgRoleLevel(orgRole) {
  return ORG_ROLE_LEVEL[orgRole] ?? 0;
}

/** Numeric project-axis level (higher = more privileged). 0 if unknown/null. */
function getProjectRoleLevel(projectRole) {
  if (projectRole == null) return 0;
  return PROJECT_ROLE_LEVEL[projectRole] ?? 0;
}

/**
 * Does the user's org role match one or more target org roles?
 * Kept compatible with the previous signature so existing callers
 * (AuthContext.hasRole) keep working against the org axis.
 * @param {string} orgRole
 * @param {string|string[]} targetRoles
 * @returns {boolean}
 */
function hasRole(orgRole, targetRoles) {
  if (!orgRole) return false;
  if (Array.isArray(targetRoles)) return targetRoles.includes(orgRole);
  return orgRole === targetRoles;
}

const isSuperAdmin = (orgRole) => orgRole === ORG_ROLE.SUPER_ADMIN;
const isAdmin = (orgRole) => orgRole === ORG_ROLE.ADMIN;
const isMember = (orgRole) => orgRole === ORG_ROLE.MEMBER;
/** The planner grant lives NEXT TO the tier (user.is_planner), composable with it. */
const isPlanner = (user) => !!(user && (user.is_planner || user.role === ORG_ROLE.SUPER_ADMIN));

// ---------------------------------------------------------------------------
// HTTP-status → UI intent. The contract the task pins down:
//   404 from a project endpoint => no access  => hide / redirect
//   403 from a project endpoint => insufficient => show but disable the control
// ---------------------------------------------------------------------------

/** What the UI should do with an access-related HTTP status. */
const ACCESS_INTENT = Object.freeze({
  HIDE: 'hide',       // pretend it does not exist (no access)
  DISABLE: 'disable', // visible but the control is inert (insufficient level)
  ALLOW: 'allow',
});

/**
 * Map an HTTP status from a project endpoint to a UI intent.
 *   404 -> HIDE     (caller has no access; hide the resource / redirect away)
 *   403 -> DISABLE  (caller can see it but lacks the level; disable the control)
 * @param {number} status
 * @returns {'hide'|'disable'|'allow'}
 */
function accessIntentForStatus(status) {
  if (status === 404) return ACCESS_INTENT.HIDE;
  if (status === 403) return ACCESS_INTENT.DISABLE;
  return ACCESS_INTENT.ALLOW;
}

// ---------------------------------------------------------------------------
// Route gate for ProtectedRoute. Org-axis only — page-level page access in the
// two-tier model is decided by org role + whether a project endpoint 404s.
// ---------------------------------------------------------------------------

/**
 * Allowed org roles for an admin-portal route requirement.
 * @param {string} requiredRole - 'admin' or 'super_admin'
 * @returns {string[]}
 */
function getAdminAllowedRoles(requiredRole) {
  if (requiredRole === ORG_ROLE.SUPER_ADMIN) return [ORG_ROLE.SUPER_ADMIN];
  return [ORG_ROLE.ADMIN, ORG_ROLE.SUPER_ADMIN];
}

/**
 * Decide what ProtectedRoute should do given auth state.
 *
 * @param {object} params
 * @param {boolean} params.isAuthenticated
 * @param {boolean} params.loading
 * @param {object|null} params.user            - expects user.role (org role)
 * @param {string}  [params.requiredRole]      - 'admin' or 'super_admin'
 * @param {string}  [params.requiredAction]    - an Action.* the route requires
 * @param {string}  [params.projectRole]       - caller's role on the active project
 * @param {string}  [params.currentPath]
 * @returns {{ action: 'loading'|'redirect'|'allow', target?: string, reason?: string }}
 */
function determineRouteAccess({
  isAuthenticated,
  loading,
  user,
  requiredRole,
  requiredAction,
  projectRole,
  currentPath,
}) {
  if (loading) return { action: 'loading', reason: 'auth-loading' };

  if (!isAuthenticated) {
    return { action: 'redirect', target: '/login', reason: 'not-authenticated' };
  }

  // Force temp password change before anything else.
  if (user?.is_temp_password && currentPath !== '/change-password') {
    return { action: 'redirect', target: '/change-password', reason: 'temp-password' };
  }

  // Admin-portal routes: org axis only.
  if (requiredRole) {
    const allowed = getAdminAllowedRoles(requiredRole);
    if (!hasRole(user?.role, allowed)) {
      return { action: 'redirect', target: '/dashboard', reason: 'insufficient-role' };
    }
  }

  // Action-gated routes: run the same decision the backend runs.
  if (requiredAction && !can(requiredAction, {
    orgRole: user?.role,
    isPlanner: !!user?.is_planner,
    projectRole,
  })) {
    return { action: 'redirect', target: '/access-restricted', reason: 'no-action-access' };
  }

  return { action: 'allow' };
}

export {
  // ORG axis
  ORG_ROLE,
  ORG_ROLE_LEVEL,
  VALID_ORG_ROLES,
  getOrgRoleLevel,
  // PROJECT axis
  PROJECT_ROLE,
  PROJECT_ROLE_LEVEL,
  GRANTABLE_PROJECT_ROLES,
  getProjectRoleLevel,
  // Actions + policy
  Action,
  ADMIN_ACTIONS,
  ACTION_REQUIRED_LEVEL,
  can,
  // Org-role helpers
  hasRole,
  isSuperAdmin,
  isAdmin,
  isMember,
  isPlanner,
  // HTTP-status → UI intent
  ACCESS_INTENT,
  accessIntentForStatus,
  // Route gate
  getAdminAllowedRoles,
  determineRouteAccess,
};
